import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Property } from '../types';
import { Link } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  shadowSize: [41, 41],
  iconSize: [25, 41],
});

interface MapViewProps {
  properties: Property[];
  center?: [number, number]; // [latitude, longitude]
  zoom?: number;
  height?: string;
  width?: string;
  selectedProperty?: string | null;
  onPropertySelect?: (propertyId: string | null) => void;
}

const defaultCenter: [number, number] = [9.7489, -83.7534]; // Costa Rica center
const defaultZoom = 7;

const MapView: React.FC<MapViewProps> = ({ 
  properties, 
  center = defaultCenter, 
  zoom = defaultZoom,
  height = '400px',
  width = '100%',
  selectedProperty = null,
  onPropertySelect = () => {},
}) => {
  const [infoWindowProperty, setInfoWindowProperty] = useState<Property | null>(
    selectedProperty ? properties.find(p => p.id === selectedProperty) || null : null
  );
  
  // Calculate center based on selected property or passed center
  const [mapCenter, setMapCenter] = useState<[number, number]>(center);

  // Handle property selection logic
  useEffect(() => {
    if (selectedProperty) {
      const property = properties.find(p => p.id === selectedProperty);
      if (property && property.coordinates) {
        setInfoWindowProperty(property);
        setMapCenter([property.coordinates.lat, property.coordinates.lng]);
      }
    } else {
      setInfoWindowProperty(null);
    }
  }, [selectedProperty, properties]);

  const handleMarkerClick = (property: Property) => {
    setInfoWindowProperty(property);
    onPropertySelect(property.id);
  };

  const filteredProperties = properties.filter(property => property.coordinates);

  return (
    <div style={{ height, width }}>
      <MapContainer 
        center={mapCenter} 
        zoom={zoom} 
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {filteredProperties.map(property => (
          <Marker 
            key={property.id} 
            position={[property.coordinates!.lat, property.coordinates!.lng]}
            icon={customIcon}
            eventHandlers={{
              click: () => handleMarkerClick(property),
            }}
          >
            <Popup>
              <div className="max-w-xs p-1">
                {property.images && property.images.length > 0 && (
                  <img 
                    src={property.images[0]} 
                    alt={property.title} 
                    className="w-full h-24 object-cover mb-2 rounded"
                  />
                )}
                <h3 className="font-semibold text-sm text-gray-800">{property.title}</h3>
                <p className="text-xs text-gray-600 mb-2">{property.location}</p>
                <p className="text-primary font-bold text-sm">${property.price.toLocaleString()}</p>
                <Link 
                  to={`/property/${property.id}`}
                  className="text-xs text-primary hover:underline block mt-2"
                >
                  View Details
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default MapView;